import {
  fmtHM,
  localDate,
  type ExceptionSeverity,
  type PeriodStatus,
  type Role,
  type TimesheetStatus,
} from '@em/shared';
import { api } from '../api';

export type Tone = 'red' | 'amber' | 'green' | 'grey' | 'accent';

/** Decimal hours as shown everywhere: 7.6 -> "7.60". */
export const hrs = (n: number | null | undefined) => (n == null ? '–' : n.toFixed(2));
export { fmtHM };

export const ROLE_LABELS: Record<Role, string> = { supervisor: 'Supervisor', payroll_admin: 'Payroll admin', owner: 'Owner' };

export const TIMESHEET_STATUS: Record<TimesheetStatus, { label: string; tone: Tone }> = {
  open: { label: 'Open', tone: 'grey' },
  ready: { label: 'Ready', tone: 'accent' },
  needs_attention: { label: 'Needs attention', tone: 'red' },
  approved: { label: 'Approved', tone: 'green' },
  rejected: { label: 'Sent back', tone: 'amber' },
  locked: { label: 'Locked', tone: 'grey' },
  exported: { label: 'Exported', tone: 'grey' },
};
export const PERIOD_STATUS: Record<PeriodStatus, { label: string; tone: Tone }> = {
  open: { label: 'Open', tone: 'accent' },
  locked: { label: 'Locked', tone: 'grey' },
  exported: { label: 'Exported', tone: 'green' },
};
export const SEVERITY: Record<ExceptionSeverity, { label: string; tone: Tone }> = {
  blocking: { label: 'Blocking', tone: 'red' },
  review: { label: 'Review', tone: 'amber' },
  info: { label: 'Info', tone: 'grey' },
};
export const SEVERITY_RANK: Record<ExceptionSeverity, number> = { blocking: 0, review: 1, info: 2 };

/** HH:MM (24 h) of a UTC instant in a zone, for prefilling time inputs. */
export function localHHMM(utc: string, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-AU', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(utc));
  const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${h === '24' ? '00' : h}:${m}`;
}

export const todayIn = (tz: string) => localDate(new Date(), tz);

/** Monday of the ISO week containing a local date. */
export function mondayOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export function deadlineText(days: number | null): { text: string; tone: Tone } {
  if (days === null) return { text: 'No deadline', tone: 'grey' };
  if (days < 0) return { text: `${Math.abs(days)} day${days === -1 ? '' : 's'} overdue`, tone: 'red' };
  if (days === 0) return { text: 'Due today', tone: 'red' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'amber' };
  return { text: `${days} days left`, tone: days <= 2 ? 'amber' : 'grey' };
}

export function minutesToHM(min: number): string {
  return fmtHM(min / 60);
}

/** Open an authenticated download (export file, PDF, CSV) in a new tab. */
export async function openDownload(path: string, onError?: (e: unknown) => void) {
  // Open the tab inside the click so popup blockers allow it, then point it at the signed link.
  const w = window.open('', '_blank');
  try {
    const url = await api.downloadUrl(path);
    if (w) {
      w.opener = null;
      w.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (e) {
    w?.close();
    if (onError) onError(e);
    else throw e;
  }
}

/** Build and download a CSV in the browser. */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: string | number | null | undefined) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([`﻿${csv}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
